import { useMemo, type FC, useCallback } from 'react';
import {
  useDisclosure
} from '@chakra-ui/react';
import copy from 'copy-to-clipboard';
import {
  UiStack,
  UiText,
  uiStyles,
  UiHStack,
  UiIconArrowSquareOut,
  UiLink,
  UiIconCopy,
  UiButton,
} from '@/lib/ui';
import dayjs from 'dayjs';
import { type Order } from '@/api/registration/order';
import { useEventQuery } from '@/registration/hook/useEventQuery';
import {
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterShareButton,
  XIcon
} from 'react-share';
import { generatePageUrl } from '@/app/pages';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import BaseBlockList from '@/base/BlockList';
import BaseDividerHorizontal from '@/base/Divider/Horizontal';
import BaseButtonPlainIcon from '@/base/Button/PlainIcon';
import { useNavigate } from 'react-router-dom';

export interface RegistrationDetailsProps {
  order: Order
}

const RegistrationDetails: FC<RegistrationDetailsProps> = ({ order }) => {
  const { isOpen, onToggle } = useDisclosure();
  const { tenantCode, eventId } = useRegisterRoute();
  const { data: event } = useEventQuery(order.eventId);
  const navigate = useNavigate();

  const url = useMemo(
    () => { return window.location.origin + generatePageUrl('RegistrationRegisterEmail', { tenantCode, eventId }); }
    , [eventId, tenantCode]);

  const title = useMemo(
    () => { return `I just registered for "${event?.name}"!`; }
    , [event?.name]);

  const copyInvoiceNumber = useCallback(
    () => { return copy(order.serial); },
    [order.serial]
  );

  return (
    <>
      <BaseBlockList
        bgColor={'#fff'}
        borderRadius={uiStyles.borderRadius}
        enableDivider={true}
        py={2}
      >
        <UiStack justifyContent={'space-between'} px={{ base: 6, lg: 8 }} py={4}>
          <UiText variant='body2' color={'text.secondary'}>Invoice number</UiText>
          <UiHStack
            onClick={copyInvoiceNumber}
          >
            <UiText variant='body1'>{order.serial}</UiText>
            {/* <UiIconCopy size={'2xl'} color={'primary.500'} weight={'bold'}/> */}
            <BaseButtonPlainIcon
              label={'Copy'}
              color={'primary.500'}
              Icon={UiIconCopy}
              {...uiStyles.hover}
            />
          </UiHStack>
        </UiStack>
        <UiStack justifyContent={'space-between'} px={{ base: 6, lg: 8 }} py={4}>
          <UiText variant='body2' color={'text.secondary'}>Name</UiText>
          <UiText variant='body1'>{order.attendee.name}</UiText>
        </UiStack>
        <UiStack justifyContent={'space-between'} px={{ base: 6, lg: 8 }} py={4}>
          <UiText variant='body2' color={'text.secondary'}>Email</UiText>
          <UiText variant='body1'>{order.attendee.email}</UiText>
        </UiStack>
        <UiStack justifyContent={'space-between'} px={{ base: 6, lg: 8 }} py={4}>
          <UiText variant='body2' color={'text.secondary'}>Event</UiText>
          <UiText variant='body1'>{order.event}</UiText>
        </UiStack>
        <UiStack justifyContent={'space-between'} px={{ base: 6, lg: 8 }} py={4}>
          <UiText variant='body2' color={'text.secondary'}>Total price</UiText>
          <UiText variant='body1'>{order.totalPriceFormat}</UiText>
        </UiStack>
        {/* <UiStack justifyContent={'space-between'} px={{base: 6, lg: 8}} py={4}> */}
        {/*  <UiText variant='body2' color={'text.secondary'}>Ordered at</UiText> */}
        {/*  <UiText variant='body1'>{order.purchasedAt ? dayjs(order.purchasedAt).format('DD MMM YYYY, h:mm A') : 'Unpaid'}</UiText> */}
        {/* </UiStack> */}
        <UiStack justifyContent={'space-between'} px={{ base: 6, lg: 8 }} py={4}>
          <UiText variant='body2' color={'text.secondary'}>Order status</UiText>
          <UiText variant='body1'>{order.status ?? 'N/A'}</UiText>
        </UiStack>
        {/* <UiHStack justifyContent={'space-between'}> */}
        {/*  <UiText variant='body1' color={'text.secondary'}>Tickets</UiText> */}
        {/* </UiHStack> */}
        {/* <UiHStack justifyContent={'space-between'}> */}
        {/*  <UiText variant='caption'>{'Name'}</UiText> */}
        {/*  <UiText variant='caption'>{'Quantity'}</UiText> */}
        {/* </UiHStack> */}
        <UiStack px={{ base: 6, lg: 8 }} py={4}>
          <UiText variant='body2' color={'text.secondary'}>Purchases</UiText>
          {order.tickets && order.tickets.length > 0 ? (
            <UiStack>
              {order.tickets.map((ticket, index) => {
                return (
                  <UiText key={index} variant='body1'>{ticket.name} (x {ticket.quantity})</UiText>
                );
              })}
            </UiStack>
          ) : (
            <UiText variant='body1'>N/A</UiText>
          )}
        </UiStack>
        {/* {order.tickets.map((ticket, index) => { */}
        {/*  return ( */}
        {/*    <UiStack key={index} justifyContent={'space-between'} px={{base: 6, lg: 8}} py={4}> */}
        {/*      <UiText variant='body1'>{ticket.name} (x {ticket.quantity})</UiText> */}
        {/*    </UiStack> */}
        {/*  ); */}
        {/* })} */}
      </BaseBlockList>
      <UiStack alignItems={'flex-end'}>
        <UiButton px={8} size={'lg'} shadow={'base'} colorScheme={'primary'} onClick={() => { return navigate(generatePageUrl('RegistrationRegisterRegistrationDetails', { tenantCode, eventId })); }}>
          View details
        </UiButton>
      </UiStack>
      <BaseDividerHorizontal height={2} />
      <UiStack spacing={6}>
        {!!event?.websiteUrl && (
          <UiStack alignItems={'flex-end'}>
            <UiLink href={event?.websiteUrl ?? undefined} target={'_blank'}>
              <UiHStack
                spacing={4}
                px={6}
                py={4}
                borderRadius={uiStyles.borderRadius}
                bgColor={'#fff'}
              >
                <UiText>{order.event} website</UiText>
                <UiStack>
                  <UiIconArrowSquareOut size={'2xl'} color={'primary.500'} />
                </UiStack>
              </UiHStack>
            </UiLink>
          </UiStack>
        )}
        <UiHStack justifyContent={'flex-end'} spacing={4}>
          <LinkedinShareButton url={url} title={title}>
            <LinkedinIcon size={40} round={true} />
          </LinkedinShareButton>
          <TwitterShareButton url={url} title={title}>
            <XIcon size={40} round={true} />
          </TwitterShareButton>
          <FacebookShareButton url={url}>
            <FacebookIcon size={40} round={true} />
          </FacebookShareButton>
        </UiHStack>
      </UiStack>
      {/* </UiSimpleGrid> */}
    </>
  );
};

export default RegistrationDetails;
